// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'
import type { Rect } from '../../types'

interface ReturningImageOverlayProps {
    src: string
    mediaType?: string
    fromRect: Rect
    toRect: Rect
    startTime: number
    onComplete: () => void
}

const ANIMATION_DURATION = 1000 // 1 second

export function ReturningImageOverlay(props: ReturningImageOverlayProps): ReactElement {
    const { src, mediaType, fromRect, toRect, startTime, onComplete } = props
    const [currentRect, setCurrentRect] = useState<Rect>(fromRect)

    useEffect(() => {
        let animationFrameId: number
        
        const animate = () => {
            const elapsed = Date.now() - startTime
            const progress = Math.min(elapsed / ANIMATION_DURATION, 1)
            
            const easeProgress = easeOutBackSoft(progress)
            
            const x = fromRect.x + (toRect.x - fromRect.x) * easeProgress
            const y = fromRect.y + (toRect.y - fromRect.y) * easeProgress
            const w = fromRect.w + (toRect.w - fromRect.w) * easeProgress
            const h = fromRect.h + (toRect.h - fromRect.h) * easeProgress
            
            setCurrentRect({ x, y, w, h })
            
            if (progress < 1) {
                animationFrameId = requestAnimationFrame(animate)
            } else {
                onComplete()
            }
        }
        
        animationFrameId = requestAnimationFrame(animate)
        
        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId)
            }
        }
    }, [fromRect, toRect, startTime, onComplete])

    const baseStyle = {
        position: 'absolute',
        left: currentRect.x,
        top: currentRect.y,
        width: currentRect.w,
        height: currentRect.h,
        opacity: 0.9,
        pointerEvents: 'none',
        objectFit: 'fill',
        zIndex: 9998,
        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2))',
    } as const

    if (mediaType === 'video') {
        return (
            <video
                src={src}
                muted
                loop
                autoPlay
                playsInline
                preload="metadata"
                draggable={false}
                style={baseStyle}
                data-canvas-ui="true"
            />
        )
    }

    return (
        <img
            src={src}
            alt="returning"
            loading="lazy"
            referrerPolicy="no-referrer"
            draggable={false}
            style={baseStyle}
            data-canvas-ui="true"
        />
    )
}

function easeOutBackSoft(t: number): number {
  const c1 = 1.2
  const c3 = c1 + 1
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
}